/**
 * Lab ClipIR + instruments → Standard MIDI File (SMF).
 * Reverse of importMidi: GM programs from the preset map; drums on ch 10.
 */
import { Midi } from "@tonejs/midi";
import type { Instrument } from "../types";
import { isCutOpenHold } from "./fx";
import { gmProgramForPreset, GM_PIANO_PROGRAM } from "./gmPresetMap";
import { gmProgramForSf2Patch, isDrumInstrument } from "../sf2/patch";
import { expandSongOrder } from "./scenes";
import {
  findPattern,
  patternSteps,
  secondsPerStep,
  type ClipIR,
  type NoteFx,
  type Pattern,
  type PatternCell,
} from "./types";

export type ExportMidiOptions = {
  /** Pattern to export (default: first pattern / active id if provided). */
  patternId?: string;
  /**
   * When true, export every pattern in song/arrangement order as one timeline
   * (ignores `patternId`).
   */
  song?: boolean;
  /** File / sequence name. */
  name?: string;
};

export type ExportMidiResult = {
  bytes: Uint8Array;
  warnings: string[];
  trackCount: number;
  noteCount: number;
};

/**
 * Encode a pattern (or the full song arrangement) as an SMF via @tonejs/midi.
 */
export function exportMidiFromProject(
  clip: ClipIR,
  instruments: Instrument[],
  options?: ExportMidiOptions,
): ExportMidiResult {
  const warnings: string[] = [];
  const patterns = options?.song
    ? expandSongOrder(clip)
        .map(id => findPattern(clip, id))
        .filter((pattern): pattern is Pattern => Boolean(pattern))
    : [
        findPattern(clip, options?.patternId) ?? clip.patterns[0],
      ].filter((pattern): pattern is Pattern => Boolean(pattern));

  if (patterns.length === 0) {
    return { bytes: new Midi().toArray(), warnings: ["No pattern to export."], trackCount: 0, noteCount: 0 };
  }

  const midi = new Midi();
  const bpm = Math.min(400, Math.max(20, Math.round(clip.bpm || 120)));
  midi.header.setTempo(bpm);
  const defaultName = options?.song
    ? "Curva Arrangement"
    : (patterns[0]!.name ?? patterns[0]!.id ?? "Curva Export");
  const name = (options?.name ?? defaultName).trim().slice(0, 64);
  midi.name = name;

  const stepSec = secondsPerStep(clip);
  let nextMelodicChannel = 0;
  let trackCount = 0;
  let noteCount = 0;
  let pianoFallbacks = 0;

  const trackCountMax = Math.max(...patterns.map(pattern => pattern.tracks.length), 0);
  for (let trackIndex = 0; trackIndex < trackCountMax; trackIndex++) {
    const instrument = instruments[trackIndex];
    const notes: ExportNote[] = [];
    let timeOffset = 0;
    let trackName = `Track ${trackIndex + 1}`;
    for (const pattern of patterns) {
      const track = pattern.tracks[trackIndex];
      if (track?.name?.trim()) {
        trackName = track.name.trim();
      }
      if (track) {
        const steps = patternSteps(pattern);
        for (const note of collectTrackNotes(track.cells, steps, stepSec)) {
          notes.push({ ...note, time: note.time + timeOffset });
        }
      }
      timeOffset += patternSteps(pattern) * stepSec;
    }
    if (notes.length === 0) {
      continue;
    }

    const midiTrack = midi.addTrack();
    midiTrack.name = trackName.slice(0, 24);
    trackCount += 1;

    if (isDrumInstrument(instrument)) {
      midiTrack.channel = 9;
      midiTrack.instrument.number = 0;
    } else if (instrument?.kind === "externalMidi") {
      midiTrack.channel = Math.min(15, Math.max(0, instrument.patch.channel - 1));
      midiTrack.instrument.number = 0;
    } else {
      midiTrack.channel = nextMelodicChannel;
      nextMelodicChannel += 1;
      if (nextMelodicChannel === 9) {
        nextMelodicChannel = 10;
      }
      if (nextMelodicChannel > 15) {
        nextMelodicChannel = 0;
      }
      const resolved = resolveMelodicProgram(instrument);
      midiTrack.instrument.number = resolved.program;
      if (resolved.fallback) {
        pianoFallbacks += 1;
      }
    }

    for (const note of notes) {
      midiTrack.addNote({
        midi: note.midi,
        time: note.time,
        duration: note.duration,
        velocity: note.velocity,
      });
      noteCount += 1;
      if (note.pan01 !== undefined) {
        midiTrack.addCC({ number: 10, time: note.time, value: note.pan01 });
      }
      if (note.mod01 !== undefined) {
        midiTrack.addCC({ number: 1, time: note.time, value: note.mod01 });
      }
      if (note.bright01 !== undefined) {
        midiTrack.addCC({ number: 74, time: note.time, value: note.bright01 });
      }
    }
  }

  if (trackCount === 0) {
    warnings.push(options?.song ? "Arrangement has no notes to export." : "Pattern has no notes to export.");
  }
  if (pianoFallbacks > 0) {
    warnings.push(
      `${pianoFallbacks} track${pianoFallbacks === 1 ? "" : "s"} → Piano (unmapped or edited preset).`,
    );
  }

  return {
    bytes: midi.toArray(),
    warnings,
    trackCount,
    noteCount,
  };
}

function resolveMelodicProgram(
  instrument: Instrument | undefined,
): { program: number; fallback: boolean } {
  if (!instrument) {
    return { program: GM_PIANO_PROGRAM, fallback: true };
  }
  if (instrument.kind === "sf2") {
    return {
      program: gmProgramForSf2Patch(instrument.patch),
      fallback: false,
    };
  }
  if (instrument.kind === "drums" || instrument.kind === "externalMidi") {
    return { program: 0, fallback: false };
  }
  if (instrument.kind === "wavetable" || instrument.kind === "sampler") {
    return { program: GM_PIANO_PROGRAM, fallback: true };
  }
  return gmProgramForPreset(instrument.preset);
}

type ExportNote = {
  midi: number;
  time: number;
  duration: number;
  velocity: number;
  pan01?: number;
  mod01?: number;
  bright01?: number;
};

function collectTrackNotes(
  cells: PatternCell[],
  steps: number,
  stepSec: number,
): ExportNote[] {
  const out: ExportNote[] = [];
  for (let step = 0; step < steps; step++) {
    const cell = cells[step];
    if (!cell || cell.note === undefined || cell.noteOff) {
      continue;
    }
    const midiNote = Math.max(0, Math.min(127, Math.round(cell.note)));
    const delayFx = cell.fx?.find(fx => fx.kind === "noteDelay");
    const delaySec = delayFx
      ? stepSec * Math.min(0.999, Math.max(0, (delayFx.value & 0xff) / 256))
      : 0;
    const time = step * stepSec + delaySec;
    const duration = noteDurationSec(cells, step, stepSec, delaySec);
    if (duration <= 0.001) {
      continue;
    }
    const note: ExportNote = {
      midi: midiNote,
      time,
      duration,
      velocity: Math.min(1, Math.max(0.05, cell.volume ?? 1)),
    };
    const pan = fxByte01(cell.fx, "pan");
    const mod = vibratoToMod01(cell.fx);
    const bright = fxByte01(cell.fx, "filter");
    if (pan !== undefined) {
      note.pan01 = pan;
    }
    if (mod !== undefined) {
      note.mod01 = mod;
    }
    if (bright !== undefined) {
      note.bright01 = bright;
    }
    out.push(note);
  }
  return out;
}

function noteDurationSec(
  cells: PatternCell[],
  step: number,
  stepSec: number,
  delaySec: number,
): number {
  const cell = cells[step]!;
  const cut = cell.fx?.find(fx => fx.kind === "cut");
  if (cut && !isCutOpenHold(cut)) {
    return Math.max(0.02, stepSec * Math.max(0.05, Math.min(16, cut.value / 16)));
  }
  if (isCutOpenHold(cut)) {
    for (let s = step + 1; s < cells.length; s++) {
      const next = cells[s];
      if (!next) {
        continue;
      }
      if (next.noteOff || next.note !== undefined) {
        return Math.max(0.02, s * stepSec - (step * stepSec + delaySec));
      }
    }
    return Math.max(0.02, cells.length * stepSec - (step * stepSec + delaySec));
  }
  return Math.max(0.02, stepSec * 1.02 - delaySec);
}

function fxByte01(fx: NoteFx[] | undefined, kind: "pan" | "filter"): number | undefined {
  const entry = fx?.find(f => f.kind === kind);
  if (!entry) {
    return undefined;
  }
  const xx = entry.value & 0xff;
  if (Math.abs(xx - 0x80) <= 4) {
    return undefined;
  }
  return xx / 255;
}

function vibratoToMod01(fx: NoteFx[] | undefined): number | undefined {
  const entry = fx?.find(f => f.kind === "vibrato");
  if (!entry) {
    return undefined;
  }
  const depth = entry.value & 0x0f;
  if (depth <= 0) {
    return undefined;
  }
  return Math.min(1, depth / 15);
}
